// SettingsCoverPane.jsx

import React, { useRef, useState } from 'react';

// Context
import { useWorkspace } from 'context/WorkspaceContext';

// Utilities
import { PhotoShape } from 'utilities/Enums';

// Services
import { clearWorkspaceCover, uploadWorkspaceCover } from 'services/workspaces/WorkspaceCoverService';

// Components
import Button from 'components/button/Button';
import WorkspaceCover from 'components/workspacecover/WorkspaceCover';

/**
 * SettingsCoverPane
 *
 * Renders upload and remove controls for the active workspace sidebar cover image.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object} workspace - Active workspace model.
 * @param {boolean} canEditCover - Whether the current user can edit workspace cover.
 *
 * @returns {ReactNode} The rendered cover settings pane.
 */
const SettingsCoverPane = React.memo(({
    theme,
    workspace,
    canEditCover,
}) => {

    // ----- CONTEXT ----- //

    // Workspace Context
    const { setActiveWorkspace } = useWorkspace();

    // ----- STATE ----- //

    // File Input Reference
    const fileInputRef = useRef(null);

    // Upload Pending State
    const [isUploading, setIsUploading] = useState(false);

    // Remove Pending State
    const [isRemoving, setIsRemoving] = useState(false);

    // Error Message State
    const [errorMessage, setErrorMessage] = useState('');

    // Status Message State
    const [statusMessage, setStatusMessage] = useState('');

    // ----- MEMOS ----- //

    // Indicates whether a persisted sidebar cover exists.
    const hasCover = typeof workspace?.sidebarCoverPhotoUrl === 'string' && workspace.sidebarCoverPhotoUrl.trim().length > 0;

    // ----- HANDLERS ----- //

    /**
     * Opens the hidden file picker when editing is allowed.
     */
    const handleChooseClick = () => {
        if (!canEditCover || isUploading || isRemoving) return;
        fileInputRef.current?.click();
    };

    /**
     * Uploads the selected workspace cover image.
     *
     * @param {object} event - Input change event.
     */
    const handleFileChange = async (event) => {
        const file = event.target?.files?.[0] || null;
        event.target.value = '';

        if (!file || !workspace?.key) return;

        setErrorMessage('');
        setStatusMessage('Uploading workspace cover...');
        setIsUploading(true);

        try {
            const nextCoverPhotoUrl = await uploadWorkspaceCover({
                workspaceKey: workspace.key,
                file,
                previousCoverPath: workspace.sidebarCoverPhotoUrl || null,
            });
            setActiveWorkspace((previous) => {
                if (!previous || previous.key !== workspace.key) return previous;
                return {
                    ...previous,
                    sidebarCoverPhotoUrl: nextCoverPhotoUrl,
                };
            });
            setStatusMessage('Workspace cover updated.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to upload workspace cover.');
            setStatusMessage('');
        } finally {
            setIsUploading(false);
        }
    };

    /**
     * Clears the persisted workspace cover path.
     */
    const handleRemoveClick = async () => {
        if (!canEditCover || !workspace?.key || !hasCover || isUploading || isRemoving) return;

        setErrorMessage('');
        setStatusMessage('Removing workspace cover...');
        setIsRemoving(true);

        try {
            await clearWorkspaceCover({
                workspaceKey: workspace.key,
                previousCoverPath: workspace.sidebarCoverPhotoUrl || null,
            });
            setActiveWorkspace((previous) => {
                if (!previous || previous.key !== workspace.key) return previous;
                return {
                    ...previous,
                    sidebarCoverPhotoUrl: null,
                };
            });
            setStatusMessage('Workspace cover removed.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to remove workspace cover.');
            setStatusMessage('');
        } finally {
            setIsRemoving(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <div className="settings-modal-pane-header">
                <div className="settings-modal-pane-header-left">
                    <div className="settings-modal-pane-title" style={{ color: theme.foreground }}>
                        Cover
                    </div>
                    <div className="settings-modal-pane-subtitle" style={{ color: theme.foregroundMuted }}>
                        Wide workspace cover used at the top of the sidebar.
                    </div>
                </div>
            </div>

            <div className="settings-modal-pane-contents">
                <div className="settings-modal-cover-card" style={{ borderColor: theme.backgroundHover }}>
                    <div className="settings-modal-cover-preview" style={{ width: '240px' }}>
                        <WorkspaceCover
                            workspace={workspace}
                            assetType="cover"
                            borderWidth={0}
                            borderRadius={10}
                            height={160}
                            shape={PhotoShape.SQUARE}
                            width="100%"
                        />
                    </div>

                    <div className="settings-modal-cover-meta">
                        <div className="settings-modal-setting-label" style={{ color: theme.foreground }}>
                            Sidebar cover
                        </div>
                        <div className="settings-modal-setting-description" style={{ color: theme.foregroundMuted }}>
                            Images keep their rectangular aspect ratio and are cropped at render time for the sidebar header.
                        </div>
                    </div>

                    <div className="settings-modal-cover-actions">
                        <Button
                            iconKey={hasCover ? 'image' : 'upload'}
                            label={hasCover ? 'Replace' : 'Upload'}
                            onClick={handleChooseClick}
                            enabled={canEditCover && !isUploading && !isRemoving}
                        />

                        <Button
                            iconKey="delete"
                            label="Remove"
                            onClick={handleRemoveClick}
                            enabled={canEditCover && hasCover && !isUploading && !isRemoving}
                        />
                    </div>
                </div>

                {!canEditCover && (
                    <div className="settings-modal-note" style={{ color: theme.foregroundMuted }}>
                        Only admins and owners can update the workspace cover.
                    </div>
                )}

                {(isUploading || isRemoving || statusMessage) && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
            </div>
        </>
    );
});

// Dev Tools
SettingsCoverPane.displayName = 'SettingsCoverPane';

export default SettingsCoverPane;